import { Component, EventEmitter, Input, Output, ElementRef, HostListener, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CitationChunk, CitationDetail } from 'src/app/models/AI/citation';

@Component({
  selector: 'medpt-citation-popover',
  templateUrl: './citation-popover.component.html',
  styleUrls: ['./citation-popover.component.scss'],
  standalone: false
})
export class CitationPopoverComponent implements OnInit, OnChanges {
  @Input() citation!: CitationDetail;
  @Input() positionX: number = 0;
  @Input() positionY: number = 0;
  @Output() chunkSelected = new EventEmitter<{ documentId: string; pageNumber: number; searchText: string; Source: string }>();
  @Output() dismissed = new EventEmitter<void>();

  sortedChunks: CitationChunk[] = [];

  constructor(private elementRef: ElementRef) {}

  ngOnInit(): void {
    this.updateSortedChunks();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['citation']) {
      this.updateSortedChunks();
    }
  }

  private updateSortedChunks(): void {
    this.sortedChunks = [...this.citation.chunks].sort((a, b) => a.pageNumber - b.pageNumber);
  }

  onChunkClick(chunk: CitationChunk): void {
    this.chunkSelected.emit({
      documentId: chunk.documentId,
      pageNumber: chunk.pageNumber,
      searchText: this.getPreview(chunk.searchText),
      Source: chunk.Source
    });
  }

  getPreview(text: string): string {
    if (!text) return '';
    const readable = this.htmlToReadableText(text);
    return readable.length > 150 ? readable.substring(0, 150) + '...' : readable;
  }

  private htmlToReadableText(html: string): string {
    // Detect table-like content (even incomplete/unclosed tables)
    if (/<t(able|r|h|d)[\s>]/i.test(html)) {
      return this.parseTableToText(html);
    }
    // For non-table HTML, strip tags and collapse whitespace
    return html.replace(/<br\s*\/?>/gi, ' | ')
      .replace(/<\/p>/gi, ' | ')
      .replace(/<\/li>/gi, ' | ')
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private parseTableToText(html: string): string {
    // Wrap in <table> if chunk is missing outer table tag
    let wrappedHtml = html;
    if (!/<table[\s>]/i.test(html)) {
      wrappedHtml = `<table>${html}</table>`;
    } else if (!/<\/table>/i.test(html)) {
      // Has opening <table but no closing </table> — close it
      wrappedHtml = html + '</table>';
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(wrappedHtml, 'text/html');
    const table = doc.querySelector('table');
    if (!table) {
      // Fallback: strip tags
      return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    }

    const rows = Array.from(table.querySelectorAll('tr'));
    const headers: string[] = [];
    const dataRows: string[][] = [];

    for (const row of rows) {
      const ths = row.querySelectorAll('th');
      if (ths.length > 0) {
        ths.forEach(th => headers.push((th.textContent || '').trim()));
      } else {
        const cells: string[] = [];
        row.querySelectorAll('td').forEach(td => cells.push((td.textContent || '').trim()));
        if (cells.length > 0) dataRows.push(cells);
      }
    }

    // If we have headers and data rows, format as "Header: Value | Header: Value"
    if (headers.length > 0 && dataRows.length > 0) {
      return dataRows.map(row =>
        row.map((cell, i) => headers[i] ? `${headers[i]}: ${cell}` : cell).join(' | ')
      ).join(' · ');
    }

    // If only headers (single-row table), join them
    if (headers.length > 0) {
      return headers.join(' | ');
    }

    // If only data rows, join cells
    return dataRows.map(row => row.join(' | ')).join(' · ');
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.dismissed.emit();
    }
  }
}
